import * as z from "zod";

/**
 * Post-purchase tools return store-relative paths under `/conversion/` and tell
 * the model not to write the link itself, so the UI owns link rendering.
 */
export interface ConversionLinkAction {
  label: string;
  url: string;
}

const CONVERSION_PATH_PREFIX = "/conversion/";

const conversionPath = z.string().startsWith(CONVERSION_PATH_PREFIX);

const UpsellPageLinkSchema = z
  .object({ status: z.literal("success"), upsellPageUrl: conversionPath })
  .passthrough();

const PostPurchaseAnalyticsLinkSchema = z
  .object({ analyticsUrl: conversionPath })
  .passthrough();

const PostPurchaseFlowLinkSchema = z
  .object({ flowViewUrl: conversionPath })
  .passthrough();

export function tryParseUpsellPageLink(
  output: unknown,
): ConversionLinkAction | null {
  const parsed = UpsellPageLinkSchema.safeParse(output);
  return parsed.success
    ? { label: "View upsell page", url: parsed.data.upsellPageUrl }
    : null;
}

export function tryParsePostPurchaseAnalyticsLink(
  output: unknown,
): ConversionLinkAction | null {
  const parsed = PostPurchaseAnalyticsLinkSchema.safeParse(output);
  return parsed.success
    ? { label: "View post-purchase analytics", url: parsed.data.analyticsUrl }
    : null;
}

export function tryParsePostPurchaseFlowLink(
  output: unknown,
): ConversionLinkAction | null {
  const parsed = PostPurchaseFlowLinkSchema.safeParse(output);
  return parsed.success
    ? { label: "View flow", url: parsed.data.flowViewUrl }
    : null;
}

const HsaKycLinkSchema = z.object({ kycPageUrl: conversionPath }).passthrough();

export function tryParseHsaKycLink(
  output: unknown,
): ConversionLinkAction | null {
  const parsed = HsaKycLinkSchema.safeParse(output);
  return parsed.success
    ? { label: "Complete identity verification", url: parsed.data.kycPageUrl }
    : null;
}

const LINK_PARSER_BY_TOOL: Record<
  string,
  (output: unknown) => ConversionLinkAction | null
> = {
  cppu__create: tryParseUpsellPageLink,
  cppu__duplicate: tryParseUpsellPageLink,
  cppf__get: tryParsePostPurchaseFlowLink,
  get_hsa_onboarding: tryParseHsaKycLink,
  pp__get_upsell_metrics: tryParsePostPurchaseAnalyticsLink,
  pp__get_upsell_product_metrics: tryParsePostPurchaseAnalyticsLink,
};

/**
 * The link a tool's output earns, if any. Rendered after the assistant's text
 * so the button reads as a follow-up rather than interrupting it.
 */
export function tryParseConversionLinkAction(
  toolName: string,
  output: unknown,
): ConversionLinkAction | null {
  return LINK_PARSER_BY_TOOL[toolName]?.(output) ?? null;
}

const CONVERSION_MARKDOWN_LINK_RE = /\[[^\]]*\]\(\/conversion\/[^)]*\)[ \t]*/gu;

/**
 * Drop model-written `/conversion/...` links: they omit the `/stores/<teamId>`
 * prefix real routes need, and the button already covers the destination.
 */
export function stripConversionMarkdownLinks(text: string): string {
  return text
    .replace(CONVERSION_MARKDOWN_LINK_RE, "")
    .replace(/\n{3,}/gu, "\n\n")
    .trimEnd();
}

/** Re-root a tool's `/conversion/...` path onto this store's conversion base. */
export function conversionHref(
  url: string,
  getConversionSubRoute: (subPath: string) => string,
): string {
  return url.startsWith(CONVERSION_PATH_PREFIX)
    ? getConversionSubRoute(url.slice(CONVERSION_PATH_PREFIX.length))
    : url;
}
